import { instance } from "api/apiBase";
import { useQuery } from "@tanstack/react-query";

// Minimal shape of a partner user needed to resolve a message sender's name.
// The `portal/v1/users` endpoint is available to every partner user (it falls
// back to a minimal entity for non-admins), so it is safe to call from the
// inbox regardless of the viewer's role.
interface OrgUser {
  id: number;
  name: string;
  email: string;
}

interface OrgUsersResponse {
  items: OrgUser[];
  page_count: number;
  total_count: number;
  has_more: boolean;
}

const orgUsersPath = "portal/v1/users";

// A single large page keeps this to one request for any realistic org size.
// If an org ever exceeds this, unresolved senders gracefully fall back to
// their raw identifier.
const ORG_USERS_PER_PAGE = 500;

const getOrgUsers = async (): Promise<OrgUser[]> => {
  const response = await instance.get<OrgUsersResponse>(orgUsersPath, {
    params: { per_page: ORG_USERS_PER_PAGE }
  });
  return response.data.items ?? [];
};

export const useOrgUsers = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [orgUsersPath, "INBOX_SENDER_NAMES"],
    queryFn: getOrgUsers,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5
  });
